'use client';

import { motion } from 'framer-motion';

interface TextSegment {
    text: string;
    className?: string;
}

interface TextRevealProps {
    text?: string;
    segments?: TextSegment[];
    className?: string;
    delay?: number;
    once?: boolean;
    priority?: boolean;
    as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | 'p';
}

const MotionComponents = {
    h1: motion.h1,
    h2: motion.h2,
    h3: motion.h3,
    h4: motion.h4,
    h5: motion.h5,
    h6: motion.h6,
    div: motion.div,
    p: motion.p
};

export default function TextReveal({
    text,
    segments,
    className = "",
    delay = 0,
    once = true,
    priority = false,
    as: Component = 'h1'
}: TextRevealProps) {
    const MotionComponent = MotionComponents[Component];
    // If text prop is provided, convert it to a single segment
    const allSegments = segments ?? (text ? [{ text }] : []);

    // Material Design 3 "Emphasized" Easing
    const emphasizedEasing = [0.2, 0.0, 0, 1.0];

    const container = {
        hidden: { opacity: 0 },
        visible: (i: number = 1) => ({
            opacity: 1,
            transition: {
                staggerChildren: priority ? 0.01 : 0.03,
                delayChildren: priority ? 0 : delay * i
            },
        }),
    };

    const child = {
        visible: {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            transition: {
                type: "tween" as const,
                ease: emphasizedEasing as [number, number, number, number],
                duration: priority ? 0.4 : 0.5,
            },
        },
        hidden: {
            opacity: priority ? 1 : 0,
            y: priority ? 0 : 12,
            filter: priority ? "none" : "blur(4px)",
            transition: {
                type: "tween" as const,
                ease: emphasizedEasing as [number, number, number, number],
                duration: priority ? 0.4 : 0.5,
            },
        },
    };

    return (
        <MotionComponent
            style={{
                overflow: "visible",
                display: "flex",
                flexWrap: "wrap",
                // Ensure text is readable even if JS fails or animations are slow
                minHeight: '1.2em'
            }}
            variants={container}
            initial={priority ? "visible" : "hidden"}
            whileInView="visible"
            viewport={{ once }}
            className={className}
        >
            {allSegments.map((segment, sIndex) => {
                // REDUCED FRAGMENTATION FOR LCP:
                // If priority is true, render the whole segment as one block
                // This ensures Lighthouse sees a single large text element instead of dozens of tiny spans.
                if (priority) {
                    return (
                        <motion.span
                            key={sIndex}
                            variants={child}
                            className={segment.className}
                            style={{ display: "inline-block", whiteSpace: "pre-wrap" }}
                        >
                            {segment.text}
                        </motion.span>
                    );
                }

                // Standard animated behavior for non-critical text
                const letters = Array.from(segment.text);
                return (
                    <span key={sIndex} className={segment.className} style={{ display: "flex", flexWrap: "wrap" }}>
                        {letters.map((letter, lIndex) => (
                            <motion.span
                                variants={child}
                                key={`${sIndex}-${lIndex}`}
                                style={{ display: "inline-block", whiteSpace: "pre" }}
                            >
                                {letter}
                            </motion.span>
                        ))}
                    </span>
                );
            })}
        </MotionComponent>
    );
}

